import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getAiInsights } from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { db, setFeatureFlag } from '@/mocks/db';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { SectionHeader, StatusBadge } from '@/components/banking';
import { CardSkeleton, EmptyState, ErrorState, LoadingRegion, Notice } from '@/components/feedback';
import { formatDateTime } from '@/services/formatters';

/**
 * AI insights — WF-SMEM-12.
 *
 * Everything here explains data the user can already see. There is deliberately
 * no control that moves money, approves anything, or changes a regulated
 * status, and no such adapter endpoint exists to call.
 *
 * TRACE:XS-SMEM-TT-R023-C4 — the assistant has an explicit Close/Back control;
 * the user is never trapped and forced to navigate away to escape it.
 *
 * Every insight carries its sources, a timestamp and a confidence level, per
 * quality/SECURITY_PRIVACY_AI_AND_ACCESSIBILITY.md ("Show sources, timestamp,
 * uncertainty and human decision").
 */
export function InsightsPage() {
  const resolved = resolveContext();
  const [dismissed, setDismissed] = useState<readonly string[]>([]);
  const [aiOff, setAiOff] = useState(false);

  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const insights = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getAiInsights(companyId, userId)).data;
  }, [companyId, userId, aiOff]);

  if (!resolved) return null;

  const flagOn = db().featureFlags.aiFinancialSummary;
  const visible = (insights.data ?? []).filter((i) => !dismissed.includes(i.id));

  const confidenceTone = (c: 'high' | 'medium' | 'low') =>
    c === 'high' ? ('success' as const) : c === 'medium' ? ('warning' as const) : ('neutral' as const);

  return (
    <>
      <MobileHeader
        title="Insights"
        backTo="/mobile/more"
        backLabel="More"
        trailing={
          <Link to="/mobile/more" className="tm-btn tm-btn--quiet tm-btn--sm">
            Close
          </Link>
        }
      />

      <div className="tm-stack">
        <Notice tone="info" title="What this can and cannot do">
          These are explanations of your own data. The assistant cannot move money, approve a
          payment, change anyone&rsquo;s access, or make a regulated decision. Every action stays
          with you.
        </Notice>

        {!flagOn ? (
          <>
            <Notice tone="warning" title="Insights are switched off">
              Your administrator has turned off AI insights. Everything else in the app works
              normally.
            </Notice>
            <Link to="/mobile/reports" className="tm-btn tm-btn--block">
              Go to reports instead
            </Link>
          </>
        ) : insights.status === 'loading' ? (
          <LoadingRegion label="Preparing insights">
            <CardSkeleton height={120} />
          </LoadingRegion>
        ) : insights.status === 'error' ? (
          <>
            {/* An AI failure must never block the manual workflow. */}
            <ErrorState
              error={insights.error}
              onRetry={insights.reload}
              title="The assistant is unavailable"
            />
            <Notice tone="info" title="Nothing else is affected">
              Your accounts, payments and approvals all work as normal while the assistant is
              unavailable.
            </Notice>
            <div className="tm-stack tm-stack--tight">
              <Link to="/mobile/reports" className="tm-btn tm-btn--block">
                Go to reports
              </Link>
              <Link to="/mobile/approvals" className="tm-btn tm-btn--secondary tm-btn--block">
                Go to approvals
              </Link>
            </div>
          </>
        ) : visible.length === 0 ? (
          <EmptyState
            title="Nothing to flag"
            body="There is nothing unusual in your recent activity. Insights appear when something changes."
          />
        ) : (
          <section aria-labelledby="ai-list">
            <SectionHeader title="What changed" id="ai-list" />
            <div className="tm-stack tm-stack--tight">
              {visible.map((insight) => (
                <article key={insight.id} className="tm-card tm-stack tm-stack--tight">
                  <div className="tm-between" style={{ alignItems: 'flex-start' }}>
                    <strong>{insight.title}</strong>
                    <StatusBadge
                      status={insight.confidence}
                      label={`${insight.confidence} confidence`}
                      tone={confidenceTone(insight.confidence)}
                    />
                  </div>
                  <p className="tm-muted" style={{ margin: 0 }}>
                    {insight.body}
                  </p>

                  {/* Sources are links the user can check for themselves. */}
                  <p className="tm-hint" style={{ margin: 0 }}>
                    Based on:{' '}
                    {insight.sources.map((s, i) => (
                      <span key={s.route}>
                        {i > 0 ? ', ' : ''}
                        <Link to={s.route}>{s.label}</Link>
                      </span>
                    ))}
                    {' · '}
                    {formatDateTime(insight.generatedAt)}
                  </p>

                  <div className="tm-inline tm-wrap">
                    {insight.sources[0] ? (
                      <Link to={insight.sources[0].route} className="tm-btn tm-btn--secondary tm-btn--sm">
                        Take a look
                      </Link>
                    ) : null}
                    <button
                      type="button"
                      className="tm-btn tm-btn--quiet tm-btn--sm"
                      onClick={() => setDismissed((d) => [...d, insight.id])}
                    >
                      Dismiss
                      <span className="tm-visually-hidden">: {insight.title}</span>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* AI-off mode: the user can turn the assistant off and keep everything
            else. Proves the manual experience is complete without AI. */}
        {flagOn ? (
          <button
            type="button"
            className="tm-btn tm-btn--secondary tm-btn--block"
            onClick={() => {
              setFeatureFlag('aiFinancialSummary', false);
              setAiOff(true);
            }}
          >
            Turn insights off
          </button>
        ) : (
          <button
            type="button"
            className="tm-btn tm-btn--secondary tm-btn--block"
            onClick={() => {
              setFeatureFlag('aiFinancialSummary', true);
              setAiOff(false);
            }}
          >
            Turn insights back on
          </button>
        )}
      </div>
    </>
  );
}
